"use client";
import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "../../Auth/context/AuthContext.js";
import { Search, Plus, Trash2, RefreshCw, Download, ChevronLeft, ChevronRight } from "lucide-react";
import Tooltip from "../../Common/components/Tooltip.jsx";

export default function IronworkersPage() {
  const { auth } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newWorker, setNewWorker] = useState({ name: "", email: "", phone: "", unionMemberId: "" });

  const limit = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get("/api/myteconstruction/contacts", {
        headers: { Authorization: `Bearer ${auth.token}` },
        params: { subType: 'Ironworker', page, limit, search: searchTerm }
      });
      setRecords(resp.data.data || []);
      setTotalPages(resp.data.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [auth.token, page, searchTerm]);

  useEffect(() => { if (auth.token) fetchData(); }, [auth.token, fetchData]);

  const handleCreate = async () => {
    try {
      // Always tag new Ironworkers as Individual/Ironworker
      const payload = { ...newWorker, contactKind: 'Individual', contactType: 'Ironworker' };
      await axios.post("/api/myteconstruction/contacts", payload, { headers: { Authorization: `Bearer ${auth.token}` } });
      setShowCreate(false);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove ironworker? This will not delete historical data.")) return;
    try {
      await axios.delete(`/api/myteconstruction/contacts/${id}`, { headers: { Authorization: `Bearer ${auth.token}` }, params: { subType: 'Ironworker' } });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const columns = ["Name", "Email", "Phone", "Union Member ID"];

  return (
    <div className="p-4 bg-gray-950 text-white min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Ironworkers</h1>
        <button onClick={() => setShowCreate(true)} className="bg-blue-600 px-3 py-1 rounded hover:bg-blue-700 flex items-center gap-1">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="mb-4 flex space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2 text-gray-400" />
          <input type="text" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 px-8 py-2 rounded" placeholder="Search..." />
        </div>
        <button onClick={fetchData} className="bg-gray-800 px-3 py-2 rounded hover:bg-gray-700">
          <RefreshCw className="h-4 w-4" />
        </button>
        <button onClick={async ()=>{
          const res = await axios.get('/api/myteconstruction/contacts/export-excel', {
            headers: { Authorization: `Bearer ${auth.token}` },
            responseType: 'blob'
          });
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ironworkers.xlsx';
          a.click();
        }}
          className="bg-gray-800 px-3 py-2 rounded hover:bg-gray-700 flex items-center gap-1">
            <Download className="h-4 w-4" />Export
        </button>
      </div>
      <div className="overflow-x-auto bg-gray-900 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-800">
            <tr>
              {columns.map(col => (
                <th key={col} className="p-2 text-left">
                  {col}{col==='Union Member ID' && <Tooltip text="Used to link a worker to their official Union record for future payroll & compliance workflows." />}
                </th>
              ))}
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length+1} className="p-4 text-center">Loading...</td></tr>
            ) : records.length ? records.map(r => (
              <tr key={r._id} className="border-b border-gray-800 hover:bg-gray-800">
                <td className="p-2">{r.name}</td>
                <td className="p-2">{r.email}</td>
                <td className="p-2">{r.phone || '—'}</td>
                <td className="p-2">
                  {r.custom?.unionMemberId ?? r.custom?.['Union Member ID'] ?? '—'}
                </td>
                <td className="p-2">
                  <button onClick={()=>handleDelete(r._id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={columns.length+1} className="p-4 text-center">No records found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages>1 && (
        <div className="flex justify-center items-center space-x-2 mt-4">
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="px-2 py-1 bg-gray-800 rounded hover:bg-gray-700"><ChevronLeft className="h-4 w-4"/></button>
          <span>{page} / {totalPages}</span>
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="px-2 py-1 bg-gray-800 rounded hover:bg-gray-700"><ChevronRight className="h-4 w-4"/></button>
        </div>
      )}
      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-gray-900 text-white p-6 rounded shadow-lg w-full max-w-sm">
            <h2 className="text-lg mb-4">Add Ironworker</h2>
            <div className="space-y-2">
              <input placeholder="Name*" value={newWorker.name} onChange={e=>setNewWorker({...newWorker,name:e.target.value})}
                className="w-full bg-gray-800 p-2 rounded" />
              <input placeholder="Email*" value={newWorker.email} onChange={e=>setNewWorker({...newWorker,email:e.target.value})}
                className="w-full bg-gray-800 p-2 rounded" />
              <input placeholder="Phone" value={newWorker.phone} onChange={e=>setNewWorker({...newWorker,phone:e.target.value})}
                className="w-full bg-gray-800 p-2 rounded" />
              <input placeholder="Union Member ID" value={newWorker.unionMemberId} onChange={e=>setNewWorker({...newWorker,unionMemberId:e.target.value})}
                className="w-full bg-gray-800 p-2 rounded" />
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={()=>setShowCreate(false)} className="px-3 py-1 bg-gray-700 rounded">Cancel</button>
              <button onClick={handleCreate} className="px-3 py-1 bg-blue-600 rounded hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}